import React, { useState } from "react";
import {
  Form,
  Select,
  InputNumber,
  Button,
  Space,
  Switch,
  Modal,
  message,
  Tag,
  Tooltip,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { MIN_DIMENSION, EDIT_COLOR } from "../utils/constants";
import { siteCentre } from "../utils/geoAlign";
import {
  canEnableGeo,
  applyAlignment,
  clearAlignment,
  geoStatus,
} from "./geoForm";
import GeoAlignView from "./GeoAlignView";
import { hasWebGL2 } from "../../LiveDashboardModal/components/GeoBackdrop";

const NO_WEBGL2 = "此瀏覽器不支援 WebGL2";
const CLEAR_GEO_CONFIRM =
  "清除對齊？場域的地圖位置與方位會回到未設定，地理定位也會關閉，按保存後生效。";
const GEO_LABEL_TIP = "啟用後，GPS 定位會放到此場域上，並可在圖層開啟地圖。";
const DISABLE_GEO_CONFIRM =
  "停用地理定位？之後的 GPS 定位不會再放到場域上，對齊設定會保留。";

const FloorPlanConfiguration = ({
  layoutType,
  setLayoutType,
  dimensions,
  setDimensions,
  imageUrl,
  uploading,
  handleUpload,
  handleRemoveImage,
  geo,
  onGeoChange,
  geoTileLayers = [],
  stations = [],
  savedDimensions,
}) => {
  const [alignOpen, setAlignOpen] = useState(false);

  const toggleGeo = (checked) => {
    if (checked) {
      onGeoChange({ ...geo, enabled: true });
      return;
    }
    Modal.confirm({
      title: "地理定位",
      content: DISABLE_GEO_CONFIRM,
      okText: "確定",
      cancelText: "取消",
      onOk: () => onGeoChange({ ...geo, enabled: false }),
    });
  };

  const clearGeo = () =>
    Modal.confirm({
      title: "清除對齊",
      content: CLEAR_GEO_CONFIRM,
      okText: "清除",
      cancelText: "取消",
      onOk: () => onGeoChange(clearAlignment(geo)),
    });

  const aligned = !!geo && canEnableGeo(geo);
  const centreTip = () => {
    const [lat, lng] = siteCentre(geo, dimensions);
    return `中心 ${lat.toFixed(5)}, ${lng.toFixed(5)} ・ 方位 ${Math.round(geo.rotationDeg)}°`;
  };

  return (
    <Form layout="inline" style={{ marginBottom: 16 }}>
      <Form.Item label="佈局：">
        <Select
          style={{ width: 160 }}
          value={layoutType}
          onChange={setLayoutType}
          options={[
            { label: "平面 (2D)", value: "plane" },
            { label: "直線 (1D)", value: "line" },
            { label: "隧道 (雙線)", value: "tunnel" },
          ]}
        />
      </Form.Item>
      <Form.Item label="寬度：">
        <InputNumber
          min={MIN_DIMENSION}
          step={1}
          style={{ width: 120 }}
          value={dimensions.width}
          onChange={(value) => {
            const numeric = typeof value === "number" ? value : 0;
            const next = numeric < MIN_DIMENSION ? MIN_DIMENSION : numeric;
            setDimensions({
              ...dimensions,
              width: next,
            });
            if (numeric < MIN_DIMENSION && numeric > 0) {
              message.warning(`寬度最小為 ${MIN_DIMENSION}m`);
            }
          }}
        />
        <span style={{ marginLeft: 8 }}>m</span>
      </Form.Item>

      <Form.Item label="高度：">
        <InputNumber
          min={MIN_DIMENSION}
          step={1}
          style={{ width: 120 }}
          disabled={layoutType === "line"}
          value={dimensions.height}
          placeholder={layoutType === "tunnel" ? "線道間距" : "高度"}
          onChange={(value) => {
            const numeric = typeof value === "number" ? value : 0;
            const next = numeric < MIN_DIMENSION ? MIN_DIMENSION : numeric;
            setDimensions({
              ...dimensions,
              height: next,
            });
            if (numeric < MIN_DIMENSION && numeric > 0) {
              message.warning(`高度最小為 ${MIN_DIMENSION}m`);
            }
          }}
        />
        <span style={{ marginLeft: 8 }}>m</span>
      </Form.Item>

      <Form.Item label="平面圖：">
        <Space>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                handleUpload(file);
              }
            }}
            style={{ display: "none" }}
            id="floor-plan-upload"
          />
          <Button
            size="small"
            type="primary"
            disabled={layoutType === "line" || layoutType === "tunnel"}
            onClick={() => document.getElementById("floor-plan-upload").click()}
            loading={uploading}
          >
            {uploading ? "上傳中..." : "上傳圖片"}
          </Button>
          {imageUrl && (
            <Button
              size="small"
              danger
              disabled={layoutType === "line" || layoutType === "tunnel"}
              onClick={handleRemoveImage}
            >
              移除圖片
            </Button>
          )}
        </Space>
      </Form.Item>

      {geo && layoutType === "plane" && (
        <Form.Item
          label={
            <span>
              地理定位
              <Tooltip title={GEO_LABEL_TIP}>
                <InfoCircleOutlined
                  aria-label="說明"
                  style={{ marginLeft: 4 }}
                />
              </Tooltip>
            </span>
          }
        >
          <Space>
            <Tooltip title={aligned ? undefined : "請先對齊地圖"}>
              <span style={{ display: "inline-block" }}>
                <Switch
                  checked={geo.enabled}
                  disabled={!aligned}
                  onChange={toggleGeo}
                />
              </span>
            </Tooltip>
            <Tooltip title={aligned ? centreTip() : undefined}>
              <Tag style={{ marginInlineEnd: 0 }}>{geoStatus(geo)}</Tag>
            </Tooltip>
            <Button
              size="small"
              disabled={!hasWebGL2()}
              title={hasWebGL2() ? undefined : NO_WEBGL2}
              onClick={() => setAlignOpen(true)}
            >
              {aligned ? "重新對齊" : "對齊地圖"}
            </Button>
            {aligned && (
              <Button type="text" size="small" onClick={clearGeo}>
                清除對齊
              </Button>
            )}
            {!hasWebGL2() && (
              <span style={{ color: "#595959" }}>{NO_WEBGL2}，無法對齊</span>
            )}
            {geo.aligned && (
              <span style={{ color: EDIT_COLOR }}>已對齊，按保存生效</span>
            )}
            {geo.cleared && (
              <span style={{ color: EDIT_COLOR }}>已清除，按保存生效</span>
            )}
          </Space>
          <GeoAlignView
            open={alignOpen}
            geo={geo}
            dimensions={dimensions}
            imageUrl={imageUrl}
            tileLayers={geoTileLayers}
            stations={stations}
            savedDimensions={savedDimensions}
            onCancel={() => setAlignOpen(false)}
            onApply={({ width, height, ...ref }) => {
              // Position, angle and size are all held in the form until 保存.
              onGeoChange(applyAlignment(geo, ref));
              setDimensions({ ...dimensions, width, height });
              setAlignOpen(false);
            }}
          />
        </Form.Item>
      )}
    </Form>
  );
};

export default FloorPlanConfiguration;
